/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionRunJS';

const ActionRunJSManager: ActionManagerFn<'ActionRunJS'> = (
  platform: Platform,
): ActionManager<'ActionRunJS'> => {
  return {
    ...baseActionManager(platform, 'ActionRunJS'),
    isChildAllowed: (page, item, childType) =>
      ['ActionOnResponse', 'ActionSetParam'].includes(childType),
    defaultValue,
    name: 'Run JS',
    props: {
      ...baseActionProps,
      code: {
        label: 'JS Code',
        type: 'typescript',
        compileOnChange: false,
        defaultScript: `import { parseISO } from 'date-fns';
import type { BaseFunctionProps } from 'cloudio';
import { recordId } from 'cloudio';

// this is the main function that get's invoke when executing
// this action. the return values can be accessed from
// child actions as $._.response.myResult

async function run(props: BaseFunctionProps) {
  const {appUid, pageId, platform} = props;

  // your Typescript code goes here
  // const store = platform.getStore(appUid, pageId, 'dsAlias');
  // const records = store.getRecords();

  // loop through the records and calculate some aggregate value
  // const sum = records.reduce((sum, row) => {
  //    return sum + row.someNumVal;
  // }, 0);

  // loop through the records and mark attributes as error
  // records.forEach((row) => {
  //   if (row.someAttr !== 'valid value') {
  //     store.setError('someAttr', recordId(row), 'error message');
  //   } else {
  //     store.clearError('someAttr', recordId(row));
  //   }
  // });

  // optionally return a json object
  return {myResult: 'some value', sum};
}

export default { run }
`,
      },
    },
    icon: { iconName: 'js-square', iconType: 'brands' },
    description: `**Overview**<br/>
The "Run JS" action is a powerful "pro-code" extensibility component that allows developers to write and execute custom TypeScript logic directly within the action workflow. Unlike the "Invoke Controller Function" action, which calls code defined in a separate file, this component embeds the script entirely within the action configuration itself.<br/><br/>
This action serves as an immediate escape hatch for scenarios where standard declarative actions or simple expressions are insufficient. It provides a fully typed environment with direct access to the application platform, enabling complex data manipulation, store interactions, and custom validation logic to be written on the fly.

**Key Features**<br/>
* **Inline Scripting:** Provides a built-in TypeScript editor, allowing developers to write, view, and modify logic without leaving the action builder context.
* **Platform Access:** The executed function receives a props object containing platform, appUid, and pageId, granting full programmatic access to Data Stores (e.g., platform.getStore), UI components, and navigation methods.
* **Return Values:** The function can return a JSON object (e.g., { result: "Success", total: 500 }). This data is captured in the $._.response variable and can be processed immediately by ActionOnResponse child actions.
* **Store Manipulation:** Capable of iterating through data store records to perform complex operations like bulk validation, setting specific field errors (store.setError), or calculating aggregate values (reduce, map).
* **Parameter Support:** Supports ActionSetParam child actions to pass dynamic arguments into the script execution context.

**Properties**<br/>
* **JS Code (code) (Required):** A TypeScript property.
  * *Function:* Contains the custom logic to be executed.
  * *Template:* Comes with a default template demonstrating how to import utilities, access stores, loop through records, and return data.

**Use Cases**<br/>
* **Complex Aggregation:** looping through a generic "Orders" data store to calculate a weighted average price based on multiple conditional factors, then returning the result to display in a summary card.
* **Advanced Validation:** iterating through a grid's records to check if the sum of all "Percentage" columns equals exactly 100%, and explicitly setting an error on specific rows that violate the rule.
* **Data Transformation:** Manually reshaping a raw API response or a complex JSON object into a flat format before passing it to a visualization component.`,
  };
};

export default ActionRunJSManager;
